import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import { PageTitle, Card, Btn, Section, Kpi, Pill } from '../components/ui-kit';

export default function ContractReview() {
  const { id } = useParams();
  const { getToken } = useXyqarAuth();
  const [data, setData] = React.useState<any>(null);
  const [err,  setErr]  = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const tok = await getToken().catch(() => '');
        const j = await apiGet<any>(`/api/v1/exec/contracts/${id}`, tok);
        setData(j);
      } catch (e: any) { setErr(e.message); }
    })();
  }, [id, getToken]);

  const previewFields = [
    ['Reference', data?.number ?? id ?? '—'],
    ['Status', data?.status ?? 'loading'],
    ['Counterparty', data?.company_name ?? data?.counterparty ?? '—'],
    ['Mission', data?.mission_id ?? data?.mission ?? '—'],
    ['Amount', data?.amount_ttc ?? data?.amount_eur ?? '—'],
    ['Created', data?.created_at ? new Date(data.created_at).toLocaleString('fr-FR') : '—'],
  ];

  return (
    <div>
      <PageTitle kicker={`Contract · ${id}`} title="Review" />

      {err && <Card className="border-red-500/30 text-red-300 text-sm mb-6">{err}</Card>}

      <Section title="Review posture" hint="This screen is for legal and operational verification before signature is opened.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Reference" value={id ? `#${id.slice(0, 8)}` : '—'} />
          <Kpi label="Status" value={data?.status ?? 'Loading…'} accent={data?.status === 'signed' ? 'green' : data?.status ? 'amber' : undefined} />
          <Kpi label="Counterparty" value={data?.company_name ?? data?.counterparty ?? '—'} />
          <Kpi label="Signature gate" value={data ? 'reviewable' : 'awaiting payload'} accent={data ? 'green' : 'amber'} />
        </div>
      </Section>

      <div className="grid gap-6 xl:grid-cols-[1.3fr,0.7fr]">
        <Card>
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-white/60">Inline PDF and section comments are not exposed by the current API payload, so this screen focuses on review metadata and handoff.</p>
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">
              {data?.status ?? 'Loading…'}
            </span>
          </div>

          <div className="rounded-md border border-dashed border-white/15 min-h-[320px] flex items-center justify-center px-8 text-center">
            <p className="text-white/30 text-sm">No server-side PDF preview is exposed in the current contract review payload. Use the metadata panel and proceed only once legal wording has been validated upstream.</p>
          </div>

          <div className="flex items-center justify-end gap-3 mt-6">
            <Link to="/contracts">
              <Btn>Back to contracts</Btn>
            </Link>
            <Link to={`/contracts/${id}/sign`}>
              <Btn primary>Proceed to signature →</Btn>
            </Link>
          </div>
        </Card>

        <div className="space-y-6">
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Review ledger</p>
            <div className="mt-4 space-y-3 text-sm text-white/80">
              {previewFields.map(([label, value]) => (
                <div key={label} className="flex items-center justify-between gap-4 border-b border-white/5 pb-2 last:border-0 last:pb-0">
                  <span className="text-white/45">{label}</span>
                  <span className="text-right font-mono text-xs text-white">{String(value)}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Review checklist</p>
            <div className="mt-4 space-y-3 text-sm text-white/80">
              <p>Confirm the counterparty, mission scope, and value before opening signature.</p>
              <p>Do not treat this page as a redline editor while the API only returns metadata.</p>
              <div className="pt-2"><Pill>{data?.status ?? 'review pending'}</Pill></div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
